import { useQuery, useQueryClient } from '@tanstack/react-query'
import { baseURL, requestMiddleware, headers } from '@/v2/constants/requests'
import { usePatientContext } from '@/v2/contexts/PatientContext'

const usePatient = ({ patientId, rest = true }) => {
  const { onSetPatient } = usePatientContext()
  const queryClient = useQueryClient()

  const {
    data,
    isLoading: isPatientLoading,
    isError: isPatientError,
  } = useQuery({
    queryKey: ['Patient', `${patientId}`],
    queryFn: async () => {
      const res = await fetch(`${baseURL}/v2/rest/patients/${patientId}`)
      if (!res.ok) {
        throw new Error('We could not find the patient you are looking for')
      }
      const data = await res.json()

      if (data) {
        onSetPatient(data)
      }

      return data
    },
    enabled: !!patientId && rest,
  })

  const {
    mutateAsync: createPatient,
    isLoading: isCreatingPatient,
    isError: isCreatingPatientError,
  } = requestMiddleware({
    key: ['CreatePatient', `${patientId}`],
    request: async patientData =>
      await fetch(`${baseURL}/v2/rest/patients`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ patient: patientData }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['Patient', `${patientId}`],
      })
    },
  })

  const {
    mutateAsync: updatePatient,
    isLoading: isUpdatingPatient,
    isError: isUpdatingPatientError,
  } = requestMiddleware({
    key: ['UpdatePatient', `${patientId}`],
    request: async patientData =>
      await fetch(`${baseURL}/v2/rest/patients/${patientId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ patient: patientData }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['Patient', `${patientId}`],
      })
    },
  })

  return {
    patient: data?.patient || null,
    patientData: data?.patient || null,
    isPatientDataLoading: isPatientLoading,
    isPatientDataError: isPatientError,
    isPatientLoading,
    isPatientError,
    updatePatient,
    isUpdatingPatient,
    isUpdatingPatientError,
    createPatient,
    isCreatingPatient,
    isCreatingPatientError,
  }
}

export default usePatient
